import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

import exercisesInitialStorage from './exercisesInitialStorage';
import pickExerciseAtCursor from './pickExerciseAtCursor';
import setCurrentExercise from '../_actions/setCurrentExercise';
import setCurriculum from '../_actions/setCurriculum';
import setCursor from '../_actions/setCursor';
import getExercises, { parseVariables } from '../_helpers/getExercises';

const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';

/**
 * Secondary filter update: change filter fields but keep the current list and
 * current exercise visible until the debounced fetch returns. Clearing the list
 * mid-request disables classicExerciseQuery and forces a full image reload.
 */
const keepListPatch = (state, patch) => ({
  ...patch,
  exercises: state.exercises,
  exercisesCount: state.exercisesCount,
  fetched: state.fetched,
  subjectExercisesCount: state.subjectExercisesCount,
  currentExercise: state.currentExercise,
  fileType: state.fileType,
  isListFetching: true,
});

/**
 * Section / subsection navigation legitimately replaces the result set.
 * Wipe the list so we do not show a stale exercise from another section, but
 * keep the previous count so the count chip does not flash empty.
 */
const sectionNavPatch = (state, patch) => ({
  ...patch,
  exerciseCursor: 0,
  currentExercise: null,
  exercises: null,
  exercisesCount: state.exercisesCount,
  mark: null,
  allExercisesCompleted: false,
  subjectExercisesCount: state.subjectExercisesCount,
  fileType: state.fileType,
  isListFetching: true,
});

/**
 * Apply list response. Preserve filter fields already set on the store.
 * If the first exercise is unchanged, keep the loaded current exercise so the
 * image/details do not reload after a filter refetch.
 */
const applyExercisesResult = (set, result) => {
  const list = result?.exercises ?? null;
  set(state => {
    const nextId = list?.[0]?.id;
    const keepCurrent = nextId != null && state.currentExercise?.id === nextId;
    return {
      ...result,
      exerciseCursor: 0,
      currentExercise: keepCurrent ? state.currentExercise : list?.[0] ?? null,
      currentExerciseShape: keepCurrent ? state.currentExerciseShape : { t: 0, o: 0, p: 0, a: 0 },
      fileType: keepCurrent ? state.fileType : 't',
      mark: keepCurrent ? state.mark : null,
      allExercisesCompleted: result?.allExercisesCompleted ?? false,
      isListFetching: false,
    };
  });
};

const store = persist(
  (set, get) => ({
    ...exercisesInitialStorage,
    fetched: false,

    // ACTIONS
    setExercisesAfterSubjectSelect: async subject => {
      set({ isListFetching: true });
      const variables = parseVariables(get(), {
        ...exercisesInitialStorage,
        subject,
      });
      const exercises = await getExercises(variables);
      set({ ...exercisesInitialStorage, ...exercises, isListFetching: false });
    },
    setSection: async section => {
      set(state =>
        sectionNavPatch(state, {
          section,
          subsection: null,
          filters: [],
          curriculum: [],
          orderBy: state.orderBy,
          userMarks: state.userMarks,
          isDone: state.isDone,
        }),
      );
      const variables = parseVariables(get(), { section, subsection: null });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setSubsection: async subsection => {
      set(state =>
        sectionNavPatch(state, {
          section: state.section,
          subsection,
          filters: state.filters,
          curriculum: state.curriculum,
          orderBy: state.orderBy,
          userMarks: state.userMarks,
          isDone: state.isDone,
        }),
      );
      const variables = parseVariables(get(), { subsection });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setSectionAndSubsection: async ({ section, subsection }) => {
      set(state =>
        sectionNavPatch(state, {
          section: section || null,
          subsection: subsection || null,
          filters: [],
          curriculum: [],
          orderBy: state.orderBy,
          userMarks: state.userMarks,
          isDone: state.isDone,
        }),
      );
      const variables = parseVariables(get(), { subsection, section });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setFilters: async filters => {
      set(state =>
        keepListPatch(state, {
          section: state.section,
          subsection: state.subsection,
          orderBy: state.orderBy,
          curriculum: state.curriculum,
          userMarks: state.userMarks,
          isDone: state.isDone,
          filters,
        }),
      );
      const variables = parseVariables(get(), { filters });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setUserMarks: async userMarks => {
      set(state =>
        keepListPatch(state, {
          section: state.section,
          subsection: state.subsection,
          orderBy: state.orderBy,
          curriculum: state.curriculum,
          filters: state.filters,
          isDone: state.isDone,
          userMarks,
        }),
      );
      const variables = parseVariables(get(), { userMarks });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setIsDone: async isDone => {
      set(state =>
        keepListPatch(state, {
          section: state.section,
          subsection: state.subsection,
          orderBy: state.orderBy,
          curriculum: state.curriculum,
          filters: state.filters,
          userMarks: state.userMarks,
          isDone,
        }),
      );
      const variables = parseVariables(get(), { isDone });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setOrder: async orderBy => {
      set(state =>
        keepListPatch(state, {
          section: state.section,
          subsection: state.subsection,
          filters: state.filters,
          curriculum: state.curriculum,
          userMarks: state.userMarks,
          isDone: state.isDone,
          orderBy,
        }),
      );
      const variables = parseVariables(get(), { orderBy });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    reload: async () => {
      const state = get();
      const variables = parseVariables(state);
      set({ exercises: null, currentExercise: null, isListFetching: true });
      const result = (await getExercises(variables)) ?? {};
      const list = result.exercises ?? null;
      set({
        ...result,
        exercises: list,
        currentExercise: pickExerciseAtCursor(list, state.exerciseCursor),
        isListFetching: false,
      });
    },
    /**
     * Reset secondary filters to defaults and keep/set dział to `section`
     * ("all" for premium, or one free dział otherwise).
     */
    clearFilters: async section => {
      const nextSection = section ?? 'all';
      set(state => {
        const patch = {
          section: nextSection,
          subsection: null,
          filters: [],
          curriculum: [],
          orderBy: exercisesInitialStorage.orderBy,
          userMarks: exercisesInitialStorage.userMarks,
          isDone: exercisesInitialStorage.isDone,
          mark: null,
          allExercisesCompleted: false,
        };
        if (state.section === nextSection) {
          return keepListPatch(state, patch);
        }
        return sectionNavPatch(state, patch);
      });
      const variables = parseVariables(get(), {
        section: nextSection,
        subsection: null,
        filters: [],
        curriculum: [],
        orderBy: exercisesInitialStorage.orderBy,
        userMarks: exercisesInitialStorage.userMarks,
        isDone: exercisesInitialStorage.isDone,
      });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setExerciseMark: mark => {
      set({ mark });
    },
    reset: () => set({ ...exercisesInitialStorage, fetched: false }),
    setFileType: fileType => set({ fileType }),
    setTotalCount: subjectExercisesCount => set({ subjectExercisesCount }),
    setCurriculum: setCurriculum(set, get),
    setExerciseCursor: setCursor(set, get),
    setCurrentExercise: setCurrentExercise(set),
  }),
  {
    name: `${import.meta.env.VITE_VERSION}_exercises`,
    getStorage: () => sessionStorage,
    // Skip the hydrated current exercise (files, sources) — JSON.stringify of it
    // on every filter click blocked the checkbox from painting.
    partialize: ({
      section,
      subsection,
      filters,
      curriculum,
      userMarks,
      isDone,
      orderBy,
      exerciseCursor,
      exercises,
      exercisesCount,
      subjectExercisesCount,
      fetched,
      fileType,
      allExercisesCompleted,
    }) => ({
      section,
      subsection,
      filters,
      curriculum,
      userMarks,
      isDone,
      orderBy,
      exerciseCursor,
      exercises,
      exercisesCount,
      subjectExercisesCount,
      fetched,
      fileType,
      allExercisesCompleted,
    }),
  },
);

const useExercisesStore = create(devtools(store, { name: 'EXERCISES', enabled: !isProduction }));

export default useExercisesStore;
